import React from 'react';
import Dropzone from 'react-dropzone';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';

import bugSeverities from '../../../../util/options/bugSeverities';
import { Notice } from '../../../../ui';
import { colors } from '../../../../theme/tokens';

export interface BugFormValues {
    topic: string;
    stepsToReproduce: string;
    expectedResult: string;
    actualResult: string;
    severity: string;
    comment: string;
}

interface Props {
    values: BugFormValues;
    errors: Set<string>;
    /* Whole form read-only (cycle and service time are over) */
    disabled?: boolean;
    /* Steps and expected result come from the test plan step and can't be changed */
    stepFieldsLocked?: boolean;
    onChange: (name: string, value: string) => void;
    onFocus: (name: string) => void;
    onBlur: (name: string, value: string) => void;

    /* Files already stored on the server (edit only) */
    existingAttachments?: React.ReactNode;
    imageFiles: any[];
    videoFiles: any[];
    onAddImages: (files: File[]) => void;
    onAddVideos: (files: File[]) => void;
    onRemoveImage: (index: number) => void;
    onRemoveVideo: (index: number) => void;
    videoMaxSize?: number;
    totalFilesSizeMB: number | string;
    maxAttachmentsSizeMB: number;
    attachmentsTooHeavy: boolean;

    submitLabel: string;
    submitDisabled: boolean;
    submitting: boolean;
    onSubmit: () => void;
}

const textFields: { name: keyof BugFormValues; label: string; multiline?: boolean; required?: boolean; stepLocked?: boolean }[] = [
    { name: 'topic', label: 'Topic', required: true },
    { name: 'stepsToReproduce', label: 'Steps to reproduce', multiline: true, required: true, stepLocked: true },
    { name: 'expectedResult', label: 'Expected result', multiline: true, required: true, stepLocked: true },
    { name: 'actualResult', label: 'Actual result', multiline: true, required: true },
];

const FilePreview: React.FC<{ name: string; onRemove?: () => void; disabled?: boolean }> = ({ name, onRemove, disabled, children }) => (
    <Box sx={{ width: 160, border: `1px solid ${colors.dividerBlue}`, borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
        <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f7fb', '& img, & video': { maxWidth: '100%', maxHeight: '100%' } }}>
            {children}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
            <Typography noWrap title={name} sx={{ flex: 1, fontSize: 12, color: colors.textGrey }}>{name}</Typography>
            {onRemove && (
                <IconButton size="small" aria-label={`Remove ${name}`} disabled={disabled} onClick={onRemove}>
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    </Box>
);

/* New / edit bug form (tester). Pure presentation: the pages own the state, validation and submit. */
const BugForm: React.FC<Props> = (props) => {
    const { values, errors, disabled = false, stepFieldsLocked = false } = props;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {textFields.map((field) => (
                <TextField
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    required={field.required}
                    value={values[field.name] || ''}
                    multiline={field.multiline}
                    minRows={field.multiline ? 3 : undefined}
                    disabled={disabled || (field.stepLocked && stepFieldsLocked)}
                    error={errors.has(field.name)}
                    helperText={errors.has(field.name) ? 'Required' : field.stepLocked && stepFieldsLocked ? 'Taken from the test plan step' : undefined}
                    // The theme paints every helper text red; only errors should be
                    FormHelperTextProps={{ sx: { color: errors.has(field.name) ? 'error.main' : 'text.secondary' } }}
                    onChange={(event) => props.onChange(field.name, event.target.value)}
                    onFocus={() => props.onFocus(field.name)}
                    onBlur={(event) => props.onBlur(field.name, event.target.value)}
                    fullWidth
                />
            ))}

            <TextField
                select
                name="severity"
                label="Severity"
                required
                value={values.severity || ''}
                disabled={disabled}
                error={errors.has('severity')}
                onChange={(event) => props.onChange('severity', event.target.value)}
                sx={{ maxWidth: 240 }}
            >
                {bugSeverities.map((option) => (
                    <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>
                ))}
            </TextField>

            <TextField
                name="comment"
                label="Additional info"
                value={values.comment || ''}
                multiline
                minRows={2}
                disabled={disabled}
                onChange={(event) => props.onChange('comment', event.target.value)}
                fullWidth
            />

            <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.titleBlue, mb: 1 }}>Attachments</Typography>

                {props.existingAttachments}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
                    <Dropzone disabled={disabled} onDrop={props.onAddImages} multiple>
                        {({ getRootProps, getInputProps }) => (
                            <Box {...getRootProps()}>
                                <input {...getInputProps()} />
                                <Button variant="outlined" startIcon={<AttachFileIcon />} disabled={disabled}>Upload attachment</Button>
                            </Box>
                        )}
                    </Dropzone>
                    <Dropzone disabled={disabled} onDrop={props.onAddVideos} multiple accept="video/mp4" maxSize={props.videoMaxSize}>
                        {({ getRootProps, getInputProps }) => (
                            <Box {...getRootProps()}>
                                <input {...getInputProps()} />
                                <Button variant="outlined" startIcon={<VideocamOutlinedIcon />} disabled={disabled}>Upload video</Button>
                            </Box>
                        )}
                    </Dropzone>
                    <Typography variant="body2" color={props.attachmentsTooHeavy ? 'error' : 'textSecondary'} sx={{ ml: 1 }}>
                        {props.totalFilesSizeMB} / {props.maxAttachmentsSizeMB} MB
                    </Typography>
                </Box>

                {(props.imageFiles.length > 0 || props.videoFiles.length > 0) && (
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {props.imageFiles.map((file, i) => (
                            <FilePreview key={`image-${i}`} name={file.lowercaseName} disabled={disabled} onRemove={() => props.onRemoveImage(i)}>
                                {file.preview && file.isImage !== false ? (
                                    <img src={file.preview} alt="" />
                                ) : (
                                    <Box sx={{ textAlign: 'center', color: colors.textGrey }}>
                                        <InsertDriveFileOutlinedIcon />
                                        <Typography sx={{ fontSize: 11 }}>{file.ext}</Typography>
                                    </Box>
                                )}
                            </FilePreview>
                        ))}
                        {props.videoFiles.map((file, i) => (
                            <FilePreview key={`video-${i}`} name={file.lowercaseName} disabled={disabled} onRemove={() => props.onRemoveVideo(i)}>
                                <video controls>
                                    <source src={file.preview} type="video/mp4" />
                                </video>
                            </FilePreview>
                        ))}
                    </Box>
                )}

                {props.attachmentsTooHeavy && (
                    <Box sx={{ mt: 1.5 }}>
                        <Notice severity="error">
                            Attachments are {props.totalFilesSizeMB} MB in total. The maximum is {props.maxAttachmentsSizeMB} MB.
                        </Notice>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" size="large" disabled={props.submitDisabled} onClick={props.onSubmit}>
                    {props.submitLabel}
                </Button>
            </Box>

            <Backdrop open={props.submitting} sx={{ zIndex: 1500, color: '#fff', flexDirection: 'column', gap: 2 }}>
                <CircularProgress color="inherit" />
                <Typography>Submitting bug…</Typography>
            </Backdrop>
        </Box>
    );
};

export default BugForm;
